<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { FilterMatchMode } from 'primevue/api';
import { DataTableFilterMeta } from 'primevue/datatable';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useToast } from 'primevue/usetoast';
import { useCaseManagerStudentStore } from '@/store';
import { StudentInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface StudentPropsInterface {
  tableData?: StudentInterface[];
  caseManagerId?: number;
  loading?: boolean;
}

interface StudentTableInterface {
  id: number;
  display_name: string;
}

const props = defineProps<StudentPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const caseManagerStudentStore = useCaseManagerStudentStore();
const students: Ref<StudentInterface[] | undefined> = toRef(props, 'tableData');
const studentCount: Ref<number> = computed(() => (students.value ? students.value.length : 0));
const studentModalOpen = ref(false);
const addStudentDataLoading = ref(false);
const toast = useToast();
const studentItems: Ref<{ name: string; value: number }[]> = ref([]);
const selectedStudents: Ref<StudentInterface[]> = ref([]);
const filters: Ref<DataTableFilterMeta> = ref({
  global: { value: '', matchMode: FilterMatchMode.CONTAINS },
});
const removeSelectedLabel = computed(() => `Remove (${selectedStudents.value.length})`);
const removingSelected = ref(false);

const createMenuModel = (item: StudentTableInterface) => {
  return [
    {
      label: 'Remove Student',
      command: () => {
        detachStudent([item.id], item.display_name);
      },
      visible: can([PERMISSIONS.CASE_MANAGER_DETACH_STUDENTS]),
    },
  ].filter((item) => item.visible);
};

const attachStudents: (ids: number[]) => Promise<void> = async (ids) => {
  await caseManagerStudentStore.attachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Student(s) has been added',
    life: 3000,
  });
};

const detachStudent: (ids: number[], studentName: string) => Promise<void> = async (ids, studentName) => {
  await caseManagerStudentStore.detachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Student ${studentName} has been removed from Case Manager`,
    life: 3000,
  });
};

const detachSelectedStudents = async () => {
  if (removingSelected.value) return;
  removingSelected.value = true;
  const count = selectedStudents.value.length;
  const ids = selectedStudents.value.map((student) => student.id);

  try {
    await caseManagerStudentStore.detachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
    emit(EMIT.UPDATE);
    toast.add({
      severity: 'success',
      summary: 'Removed',
      detail: `${count} student${count === 1 ? '' : 's'} removed from Case Manager`,
      life: 3000,
    });
  } finally {
    selectedStudents.value = [];
    removingSelected.value = false;
  }
};

const openAddStudents = async () => {
  studentItems.value = [];
  if (can([PERMISSIONS.CASE_MANAGER_STUDENTS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.CASE_MANAGER_ATTACH_STUDENTS])) {
    addStudentDataLoading.value = true;
    studentModalOpen.value = true;
    studentItems.value = await caseManagerStudentStore.getAddStudentsModalData(props.caseManagerId as number);
    addStudentDataLoading.value = false;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add students',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  const studentIds = students.map((student) => student.value);
  attachStudents(studentIds);
  studentModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Students</h2>
      <Chip>{{ studentCount }}</Chip>
      <IconField icon-position="left" class="ml-3">
        <InputIcon>
          <Icon name="search" class="text-lg" />
        </InputIcon>
        <InputText v-model="filters.global.value" class="p-inputtext-sm" placeholder="Search students" />
      </IconField>
      <div class="ml-auto flex">
        <AppButton
          v-if="can([PERMISSIONS.CASE_MANAGER_ATTACH_STUDENTS])"
          type="text"
          size="small"
          text="Add Students"
          icon-left="add"
          @click="openAddStudents"
        />
        <AppButton
          v-if="can([PERMISSIONS.CASE_MANAGER_DETACH_STUDENTS]) && selectedStudents.length"
          type="alert"
          :text="removeSelectedLabel"
          :loading="removingSelected"
          :disabled="removingSelected"
          @click="detachSelectedStudents"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:filters="filters"
        v-model:selection="selectedStudents"
        :value="students"
        :global-filter-fields="['display_name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="id"
        responsive-layout="scroll"
        scroll-height="440px"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="studentCount">
          <Column
            v-if="can([PERMISSIONS.CASE_MANAGER_DETACH_STUDENTS])"
            selection-mode="multiple"
            header-style="justify-content: center; max-width: 3rem;"
            body-style="justify-content: center; max-width: 3rem;"
          />
          <Column body-class="mw-20rem" header-class="mw-20rem" field="display_name" header="Student Name" sortable>
            <template #body="{ data }">
              <a class="font-semibold" :href="`/user/${data.id}`" v-text="data.display_name" />
            </template>
          </Column>
          <Column />
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Students not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="studentModalOpen && can([PERMISSIONS.CASE_MANAGER_STUDENTS_AVAILABLE_TO_ADD])"
        title="Add Students"
        button-text="Add Students"
        placeholder="Search students"
        :loading="addStudentDataLoading"
        :items="studentItems"
        :searchable="true"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>
